import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CopyLink } from '@assets/svg/copyIcon.svg';
import { ReactComponent as DetailsIcon } from '@assets/svg/details.svg';
import { ReactComponent as EditPencilIcon } from '@assets/svg/ic-edit-pencil.svg';
import { ReactComponent as LetterIcon } from '@assets/svg/letter.svg';
import { ReactComponent as SnoozeIcon } from '@assets/svg/snooze.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IActionItemGlobalItemOrId } from '@model/actionItem';

import { useCancelSnoozeMutation } from '@pages/ActionItem/services/useCancelSnoozeMutation';

import { useSetReadMutation } from '@services/useActionItemsReadMutation';
import { useSetUnReadMutation } from '@services/useActionItemsUnReadMutation';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { selectActionItemsViewed, useActionItemsStoreAction } from '../../store/useActionItemsStore';
import style from './style.module.scss';

interface IProps {
  item: IActionItemGlobalItemOrId;
}

const ActionItemTableMenu: React.FC<IProps> = ({ item }) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();

  const { data: profileData } = useUserProfileQuery();

  const viewedAIId = selectActionItemsViewed();
  const { setActionItemsUnViewedId, setActionItemsViewedId } = useActionItemsStoreAction();
  const [contextMenuId, setContextMenuId] = useState<number | null>(null);
  const { setActiveModal, setSecondaryModal } = useModalActions();

  const { mutate: setReadMutate } = useSetReadMutation();
  const { mutate: setUnReadMutate } = useSetUnReadMutation();
  const { mutate: cancelSnoozeMutate } = useCancelSnoozeMutation({
    actionItemId: item.id,
  });

  const hasPermissions = profileData?.role !== 'Viewer' && item.point_type !== 'boolean';

  const viewAiDetails = (id: number) => {
    navigate(`/customer/${customerId}/ai/${id}`, {
      state: { prevPage: `/customer/${customerId}` },
    });
  };

  const onShare = (id: number) => {
    const url = `${window.location.origin}/customer/${customerId}/ai/${id}`;

    try {
      navigator.clipboard.writeText(url).then(() => {
        showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
      });
    } catch (e) {
      console.log(e);
    }
  };

  const cancelSnooze = (id: number) => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Remove snooze'),
        bodyContent: t('Are you sure you want to cancel Action Item snooze?'),

        onConfirm: () =>
          cancelSnoozeMutate(
            { action_item_id: id },
            {
              onSuccess: () => {
                showToast({
                  variant: 'success',
                  message: t('The snooze that was applied to the action item has been canceled.'),
                });
              },
            },
          ),
      },
    });
  };

  const editSnooze = (item: IActionItemGlobalItemOrId) => {
    setActiveModal({
      name: MODAL_NAMES.editSnoozeModal,
      options: {
        actionItemId: +item.id,
        facilityTimezone: item.facilityOffset,
        remindDate: item.remindDate || '',
      },
    });
  };

  const itemIsViewed = viewedAIId[item.id] !== undefined ? !viewedAIId[item.id] : !item.is_viewed;

  const handleReadUnRead = (item: IActionItemGlobalItemOrId) => {
    if (itemIsViewed) {
      setReadMutate(
        { action_list_id: +item.id },
        {
          onSuccess: () => {
            setActionItemsViewedId(+item.id);
          },
        },
      );
    } else {
      setUnReadMutate(
        { action_list_id: +item.id },
        {
          onSuccess: () => {
            setActionItemsUnViewedId(+item.id);
          },
        },
      );
    }
  };

  return (
    <div
      tabIndex={0}
      className={style.tableBodyRowMenu}
      onClick={() => setContextMenuId(item.id)}
      onBlur={() => setContextMenuId(null)}
    >
      <ThreeDotsIcon />
      {contextMenuId === item.id && (
        <div className={style.tableBodyRowMenuPopover}>
          <div className={style.tableBodyRowMenuPopoverItem} onClick={() => onShare(item?.id)}>
            <CopyLink className={style.menuIcon} />
            {t('Copy link')}
          </div>
          <div className={style.tableBodyRowMenuPopoverItem} onClick={() => handleReadUnRead(item)}>
            <LetterIcon className={style.menuIcon} />
            {itemIsViewed ? t('Mark as read') : t('Mark as unread')}
          </div>
          <div onClick={() => viewAiDetails(item.id)} className={style.tableBodyRowMenuPopoverItem}>
            <DetailsIcon className={style.menuIcon} />
            {t('View details')}
          </div>
          {hasPermissions && (
            <>
              {!!item.is_troubleshooting_hold ||
                (item.stateName !== 'Verifying' && item.stateName !== 'Snoozed' && (
                  <div
                    className={style.tableBodyRowMenuPopoverItem}
                    onClick={() =>
                      setActiveModal({
                        name: MODAL_NAMES.snoozeModal,
                        options: {
                          actionItemId: item.id,
                          facilityTimezone: item.facilityOffset,
                        },
                      })
                    }
                  >
                    <SnoozeIcon className={style.menuIcon} />
                    {t('Snooze')}
                  </div>
                ))}
              {item.stateName === 'Snoozed' && (
                <div className={style.tableBodyRowMenuPopoverItem} onClick={() => editSnooze(item)}>
                  <EditPencilIcon className={style.menuIcon} />
                  {t('Edit snooze')}
                </div>
              )}
              {item.stateName === 'Snoozed' && (
                <div className={style.tableBodyRowMenuPopoverItem} onClick={() => cancelSnooze(+item.id)}>
                  <SnoozeIcon className={style.menuIcon} />
                  {t('Cancel snooze')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionItemTableMenu;
